import { Add, ArrowRight, Subtract } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { Button } from 'DesignSystem/ui/button';
import { Input, InputProps } from 'DesignSystem/ui/input';
import { Label } from 'DesignSystem/ui/label';
import { Skeleton } from 'DesignSystem/ui/skeleton';
import { cn } from 'DesignSystem/utils';
import { prefCardsBootstrapQuery, usePrefCardsBootstrapQuery } from 'pages/preference-cards/api';
import { usePrefCardProcedureConflictsQuery } from 'pages/preference-cards/api/preference-card-conflicts';
import { getActionTypeId, selectActionNames } from 'pages/preference-cards/api/selectors';

import { useAddConflictChange, useConflictChanges, useRemoveConflictChange } from '../../conflicts/store';

export function ModifyQuantity(props: { cardId: string; itemId: string }) {
  const declineReason = usePrefCardsBootstrapQuery((data) =>
    data.procedure_card_preference_card_client_item_master_override_reasons.find(
      (reason) => reason.display_name === 'Quantity Modified'
    )
  );
  const { data: items, isLoading } = usePrefCardProcedureConflictsQuery(props.cardId, (data) =>
    data.filter((item) => item.pccim_id === props.itemId)
  );
  const add = useAddConflictChange();
  const remove = useRemoveConflictChange();
  const [item] = items ?? [];
  const { data: actionsData } = useQuery(prefCardsBootstrapQuery(selectActionNames));
  const changes = useConflictChanges();
  const existingItemChange = changes[item?.pccim_id];
  const [validationError, setValidationError] = React.useState<string | null>(null);

  if (isLoading || declineReason.isLoading) return <Skeleton className="h-[224px] w-full" />;
  if (!item || !declineReason.data) return null;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (!declineReason.data || !actionsData) return;
    if (existingItemChange) return;
    event.preventDefault();
    setValidationError(null);

    const formData = new FormData(event.currentTarget);
    const unit_open = Number(formData.get('open_qty'));
    const unit_hold = Number(formData.get('prn_qty'));

    // Check if the new values match the original values (no net change)
    if (unit_open === item.old_unit_open && unit_hold === item.old_unit_hold) {
      setValidationError('No changes detected. The quantities match the original values.');
      return;
    }

    const isOverride = unit_open !== item.new_unit_open || unit_hold !== item.new_unit_hold;

    // Calculate the correct action type based on the new values
    const actionTypeId = getActionTypeId(unit_open, unit_hold, actionsData, {
      originalOpen: item.old_unit_open,
      originalHold: item.old_unit_hold,
      recommendedOpen: item.new_unit_open,
      recommendedHold: item.new_unit_hold,
      hasRecommendation: true, // Conflicts always have recommendations
    });

    add({
      originalItem: item,
      changes: isOverride
        ? {
            action_type_id: actionTypeId,
            preference_card_client_item_master_id: props.itemId,
            unit_hold,
            unit_open,
            create_override: true,
            procedure_card_pccim_override_reason_id: declineReason.data.procedure_card_pccim_override_reason_id,
          }
        : {
            action_type_id: actionTypeId,
            preference_card_client_item_master_id: props.itemId,
            unit_hold,
            unit_open,
            create_override: false,
          },
    });
  }

  function handleCancel() {
    remove(item.pccim_id);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="grid grid-rows-[auto_auto] overflow-hidden rounded-md bg-zinc-50 shadow-[inset_0_0_2px_0_rgba(0,0,0,0.06)]">
        <div className="grid grid-cols-2 bg-indigo-50 py-3 text-center">
          <Label htmlFor="open_qty" className="type-body-xs select-none px-2">
            Open Qty
          </Label>
          <Label htmlFor="prn_qty" className="type-body-xs select-none px-2">
            PRN Qty
          </Label>
        </div>
        <div className="grid grid-cols-2 py-2">
          <div className="px-3">
            <QuantityInput
              key={existingItemChange?.originalItem.pccim_id}
              disabled={Boolean(existingItemChange)}
              name="open_qty"
              id="open_qty"
              previousValue={item.old_unit_open}
              defaultValue={existingItemChange?.changes.unit_open ?? item.new_unit_open}
            />
          </div>
          <div className="px-3">
            <QuantityInput
              key={existingItemChange?.originalItem.pccim_id}
              disabled={Boolean(existingItemChange)}
              name="prn_qty"
              id="prn_qty"
              previousValue={item.old_unit_hold}
              defaultValue={existingItemChange?.changes.unit_hold ?? item.new_unit_hold}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          disabled={!existingItemChange}
          variant="destructive-ghost"
          onClick={handleCancel}
          className="select-none border border-destructive"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={Boolean(existingItemChange)} className="select-none">
          Save Changes
        </Button>
      </div>

      {validationError && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-sm text-amber-800">{validationError}</p>
        </div>
      )}
    </form>
  );
}

function QuantityInput(props: InputProps & { previousValue?: number; max?: number; min?: number }) {
  const { previousValue: _, ...inputProps } = props;
  const [value, setValue] = React.useState(props.defaultValue ?? 0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  function handleAddition() {
    if (!inputRef.current) return;

    const current = Number(inputRef.current.value) || 0;
    const next = current >= 0 ? current + 1 : 0;
    setValue(next);
    inputRef.current.value = next.toString();
  }

  function handleSubtraction() {
    if (!inputRef.current) return;

    const current = Number(inputRef.current.value) || 0;
    const next = current > 0 ? current - 1 : 0;
    setValue(next);
    inputRef.current.value = next.toString();
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const next = Number(event.currentTarget.value) || 0;
    if (next > (props.max ?? 0) || next < (props.min ?? 0)) return;
    setValue(next);
    props.onChange?.(event);
  }

  return (
    <label htmlFor={props.name} className="flex h-[50px] items-center rounded-[5px] border bg-white p-2">
      <div className="flex w-full justify-center">
        <div className="grid grid-cols-[min-content_min-content] items-center">
          {typeof props.previousValue === 'number' && props.previousValue !== value ? (
            <span className="flex select-none items-center gap-x-1 text-zinc-500">
              <span>{props.previousValue}</span>
              <ArrowRight className="size-3.5" />
            </span>
          ) : null}

          <Input
            ref={inputRef}
            type="number"
            variant="ghost"
            {...inputProps}
            onChange={handleChange}
            defaultValue={props.defaultValue ?? 0}
            className={cn(
              'm-0 h-5 min-w-[2ch] border-none p-0 text-center leading-5 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
              props.className
            )}
          />
        </div>
      </div>

      {props.disabled ? null : (
        <div className="flex flex-col items-center justify-center rounded-sm text-indigo-700 shadow">
          <button type="button" className="select-none border-b hover:bg-zinc-100" onClick={handleAddition}>
            <Add className="h-4 w-4" />
          </button>
          <button type="button" className="select-none hover:bg-zinc-100" onClick={handleSubtraction}>
            <Subtract className="h-4 w-4" />
          </button>
        </div>
      )}
    </label>
  );
}
